import { ChevronDown, ChevronRight, Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import citriniService from "../../../../services/citriniService";
import { CitriniBasketListItem, CitriniBasketWithDetails } from "../../../../types/citrini";
import { EmptyState, ErrorBanner, formatDateCell, LoadingSpinner } from "../helpers";

interface BasketsFetchedTabProps {
  fetchInfoId: number;
  isDarkMode: boolean;
  onCountChange?: (count: number) => void;
}

const BasketsFetchedTab: React.FC<BasketsFetchedTabProps> = ({ fetchInfoId, isDarkMode, onCountChange }) => {
  const [baskets, setBaskets] = useState<CitriniBasketListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedData, setExpandedData] = useState<Map<number, CitriniBasketWithDetails>>(
    new Map()
  );
  const [expandLoading, setExpandLoading] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await citriniService.listBasketsByFetchInfoId(fetchInfoId);
      setBaskets(res);
      onCountChange?.(res.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load baskets");
    } finally {
      setLoading(false);
    }
  }, [fetchInfoId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRow = useCallback(
    async (basket: CitriniBasketListItem) => {
      const id = basket.id;
      setExpandedRows((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });

      // Fetch detail on first expand
      if (!expandedData.has(id) && !expandLoading.has(id)) {
        setExpandLoading((prev) => new Set(prev).add(id));
        try {
          const detail = await citriniService.getBasketById(id);
          setExpandedData((prev) => new Map(prev).set(id, detail));
        } catch {
          // Silently fail - row will show no data
        } finally {
          setExpandLoading((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      }
    },
    [expandedData, expandLoading]
  );

  const expandAll = useCallback(async () => {
    const ids = new Set(baskets.map((b) => b.id));
    setExpandedRows(ids);
    // Fetch details for all unexpanded
    const toFetch = baskets.filter((b) => !expandedData.has(b.id) && !expandLoading.has(b.id));
    if (toFetch.length === 0) return;
    const loadingIds = new Set(toFetch.map((b) => b.id));
    setExpandLoading((prev) => new Set([...prev, ...loadingIds]));
    await Promise.all(
      toFetch.map(async (b) => {
        try {
          const detail = await citriniService.getBasketById(b.id);
          setExpandedData((prev) => new Map(prev).set(b.id, detail));
        } catch {
          // skip
        } finally {
          setExpandLoading((prev) => {
            const next = new Set(prev);
            next.delete(b.id);
            return next;
          });
        }
      })
    );
  }, [baskets, expandedData, expandLoading]);

  const collapseAll = useCallback(() => {
    setExpandedRows(new Set());
  }, []);

  const [searchTerm, setSearchTerm] = useState("");

  const filteredBaskets = React.useMemo(() => {
    if (!searchTerm.trim()) return baskets;
    const term = searchTerm.toLowerCase();
    return baskets.filter(
      (b) => b.citriniId.toLowerCase().includes(term) || b.name.toLowerCase().includes(term)
    );
  }, [baskets, searchTerm]);

  if (loading) {
    return <LoadingSpinner isDarkMode={isDarkMode} />;
  }

  if (error) {
    return <ErrorBanner message={error} onRetry={load} isDarkMode={isDarkMode} />;
  }

  if (baskets.length === 0) {
    return <EmptyState message="No baskets fetched in this run" isDarkMode={isDarkMode} />;
  }

  const COLS = 6;

  return (
    <div>
      {/* Search and controls */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
            {filteredBaskets.length === baskets.length
              ? `${baskets.length} basket${baskets.length !== 1 ? "s" : ""}`
              : `${filteredBaskets.length} of ${baskets.length}`}
          </span>
          <div className="relative">
            <Search className={`w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`} />
            <input
              type="text"
              placeholder="Search by ID or name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`text-xs rounded-lg border pl-8 pr-3 py-1.5 w-52 ${
                isDarkMode
                  ? "bg-rp-surface border-rp-separator text-rp-grey/70 placeholder-rp-grey/40"
                  : "bg-white border-gray-300 text-gray-700 placeholder-gray-400"
              }`}
            />
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={expandAll}
            className={`text-xs px-2 py-1 rounded ${
              isDarkMode ? "text-rp-grey/50 hover:bg-rp-surface" : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className={`text-xs px-2 py-1 rounded ${
              isDarkMode ? "text-rp-grey/50 hover:bg-rp-surface" : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            Collapse All
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr
              className={`text-left text-xs ${
                isDarkMode
                  ? "text-rp-grey/50 border-b border-rp-separator"
                  : "text-gray-500 border-b border-gray-200"
              }`}
            >
              <th className="pb-2 pr-2 font-medium w-8" />
              <th className="pb-2 pr-4 font-medium">ID</th>
              <th className="pb-2 pr-4 font-medium">Citrini ID</th>
              <th className="pb-2 pr-4 font-medium">Name</th>
              <th className="pb-2 pr-4 font-medium">Inception Date</th>
              <th className="pb-2 font-medium">Fetched At</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            {filteredBaskets.map((basket) => {
              const isExpanded = expandedRows.has(basket.id);
              const detail = expandedData.get(basket.id);
              const isLoadingDetail = expandLoading.has(basket.id);

              return (
                <React.Fragment key={basket.id}>
                  <tr
                    onClick={() => toggleRow(basket)}
                    className={`cursor-pointer ${
                      isDarkMode ? "hover:bg-rp-surface/50" : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="py-2.5 pr-2">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {basket.id}
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {basket.citriniId}
                    </td>
                    <td
                      className={`py-2.5 pr-4 ${isDarkMode ? "text-rp-grey" : "text-gray-800"}`}
                    >
                      {basket.name}
                    </td>
                    <td
                      className={`py-2.5 pr-4 ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}
                    >
                      {formatDateCell(basket.inceptionDateUtc)}
                    </td>
                    <td
                      className={`py-2.5 ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}
                    >
                      {formatDateCell(basket.fetchedAtUtc)}
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td colSpan={COLS} className="p-0">
                        <div
                          className={`px-8 py-3 ${
                            isDarkMode ? "bg-rp-surface/30" : "bg-gray-50"
                          }`}
                        >
                          {isLoadingDetail && (
                            <div className="flex items-center py-4">
                              <div
                                className={`animate-spin rounded-full h-5 w-5 border-b-2 mr-3 ${
                                  isDarkMode ? "border-rp-amber" : "border-blue-600"
                                }`}
                              />
                              <span
                                className={`text-sm ${
                                  isDarkMode ? "text-rp-grey/50" : "text-gray-600"
                                }`}
                              >
                                Loading holdings...
                              </span>
                            </div>
                          )}
                          {detail && detail.citriniBasketDetails.length === 0 && (
                            <p
                              className={`text-sm py-2 ${
                                isDarkMode ? "text-rp-grey/40" : "text-gray-400"
                              }`}
                            >
                              No holdings
                            </p>
                          )}
                          {detail && detail.citriniBasketDetails.length > 0 && (
                            <table className="w-full text-xs">
                              <thead>
                                <tr
                                  className={`text-left ${
                                    isDarkMode
                                      ? "text-rp-grey/40 border-b border-rp-separator"
                                      : "text-gray-400 border-b border-gray-200"
                                  }`}
                                >
                                  <th className="pb-1.5 pr-3 font-medium">Ticker</th>
                                  <th className="pb-1.5 pr-3 font-medium">Name</th>
                                  <th className="pb-1.5 pr-3 font-medium">FIGI</th>
                                  <th className="pb-1.5 pr-3 font-medium">ISIN</th>
                                  <th className="pb-1.5 pr-3 font-medium">MIC</th>
                                  <th className="pb-1.5 pr-3 font-medium text-right">Weight</th>
                                  <th className="pb-1.5 font-medium">Date</th>
                                </tr>
                              </thead>
                              <tbody
                                className={`divide-y ${
                                  isDarkMode ? "divide-rp-separator/50" : "divide-gray-100"
                                }`}
                              >
                                {detail.citriniBasketDetails.map((row) => (
                                  <tr key={row.id}>
                                    <td
                                      className={`py-1.5 pr-3 font-mono ${
                                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                                      }`}
                                    >
                                      {row.ticker}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 ${
                                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                                      }`}
                                    >
                                      {row.name}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 font-mono ${
                                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                      }`}
                                    >
                                      {`${row.compositeFigi ?? "null"},${row.data?.chinaC1Figi ?? "null"},${row.data?.chinaC2Figi ?? "null"}`}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 font-mono ${
                                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                      }`}
                                    >
                                      {row.isin || "-"}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 font-mono ${
                                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                      }`}
                                    >
                                      {row.mic || "-"}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 text-right font-mono ${
                                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                                      }`}
                                    >
                                      {row.weight}
                                    </td>
                                    <td
                                      className={`py-1.5 font-mono ${
                                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                      }`}
                                    >
                                      {row.date}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BasketsFetchedTab;
